import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import IosShareIcon from "@mui/icons-material/IosShare";
import { useTranslation } from "react-i18next";
import copyText from "core/utils/copyText";
import { useLazyQuery } from "@apollo/client";
import { ContentEmbedCodeQuery } from "core/queries";
import { durationFormat } from "core/utils/string";
import {
  parseStartTime,
  urlWithStart,
  embedWithStart,
} from "core/utils/shareStartTime";

/**
 * Окно «Поделиться» по макету «ВидеоСпортс UI» (Figma, EmbedVideo,
 * раздел «Поделиться»): ссылка и embed-код, у каждого своя кнопка
 * копирования, которая после нажатия на пару секунд показывает галочку.
 *
 * Поверх макета: «Начать с» (ссылка и код с текущей секундой) и системное
 * меню «Другие приложения» на телефонах.
 *
 * Общий компонент для кнопки «Поделиться» эмбеда (core/components/ShareButton)
 * и кнопки «Поделиться» страницы видео на сайте (arena/components/ShareButton).
 * В эмбеде окно открывается внутри маленького iframe, выйти за его границы
 * оно не может, поэтому:
 * - в узком или невысоком окне диалог раскрывается на весь iframe (на
 *   большой странице сайта это соответствующие размеры окна браузера);
 * - в совсем низком (эмбед 16:9 на телефоне, ~200px) поля с текстом
 *   прячутся и остаются только кнопки, иначе ничего не помещается.
 */

/** Цвета из макета. */
const UI = {
  bg: "#222",
  overlay: "rgba(34,34,34,0.6)",
  border: "#505050",
  content: "#d8d8d8",
  error: "#e5484d",
  primary: "#00C78B",
  primaryActive: "#00A876",
};

/** Сколько держим галочку «скопировано» на кнопке. */
const COPIED_TIMEOUT = 2000;

const Label = styled(Typography)({
  color: UI.content,
  fontSize: 14,
  lineHeight: "20px",
  fontWeight: 500,
});

const Field = styled(InputBase)({
  width: "100%",
  height: 40,
  padding: "0 12px",
  border: `1px solid ${UI.border}`,
  borderRadius: 8,
  backgroundColor: UI.bg,
  color: UI.content,
  fontSize: 16,
  lineHeight: "24px",
  "& input": {
    padding: 0,
    textOverflow: "ellipsis",
  },
});

const ActionButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== "done" && prop !== "outlined",
})(({ done, outlined }) => ({
  width: "100%",
  minWidth: 0,
  minHeight: 40,
  whiteSpace: "normal",
  gap: 4,
  padding: "8px 16px",
  borderRadius: 8,
  fontSize: 14,
  lineHeight: "24px",
  fontWeight: 500,
  color: "#fff",
  textAlign: "center",
  transition: "background-color .15s ease",
  ...(outlined
    ? {
        border: `1px solid ${UI.border}`,
        "&:hover": { backgroundColor: "rgba(255,255,255,0.06)" },
      }
    : {
        backgroundColor: done ? UI.primaryActive : UI.primary,
        "&:hover": { backgroundColor: UI.primaryActive },
      }),
  "& svg": { fontSize: 16 },
}));

function CopyButton({ done, onClick, doneText, children }) {
  return (
    <ActionButton done={done} onClick={onClick}>
      {done && <CheckIcon />}
      {done ? doneText : children}
    </ActionButton>
  );
}

CopyButton.propTypes = {
  done: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  doneText: PropTypes.string.isRequired,
  children: PropTypes.node,
};

function ShareDialog({
  open,
  onClose,
  contentId,
  shareText,
  shareUrl: baseShareUrl,
  getCurrentTime,
  // MUI: true отключает блокировку скролла страницы под модалкой. В эмбеде
  // это и раньше было true безусловно (окно живёт в iframe, блокировать
  // скролл хост-страницы снаружи него смысла не имеет). На странице сайта
  // диалог открывается не в iframe, и arena ShareButton передаёт false,
  // чтобы страница не скроллилась под открытым диалогом.
  disableScrollLock = true,
}) {
  const { t } = useTranslation("core");

  const [copied, setCopied] = useState(null);
  const copiedTimer = useRef();

  // В эмбеде размеры медиазапросов это размеры iframe, на странице сайта
  // размеры окна браузера.
  // Карточка из макета 469×392: в эмбеде 744×419 она помещается по центру
  const fullScreen = useMediaQuery("(max-width: 519px), (max-height: 399px)");
  const compact = useMediaQuery("(max-height: 359px)");

  // «Начать с»: при открытии подставляем текущую секунду плеера,
  // галочкой её можно включить и поправить вручную
  const [startEnabled, setStartEnabled] = useState(false);
  const [startText, setStartText] = useState("00:00");
  const startSeconds = startEnabled ? parseStartTime(startText) : null;

  // Системное меню «Поделиться» (телефоны, Safari, Chrome на десктопе).
  // Embed-кода в нём нет, поэтому оно идёт отдельной кнопкой в нашем окне.
  const canNativeShare = process.browser && Boolean(navigator.share);

  const [loadEmbedCode, { data: embedData }] = useLazyQuery(
    ContentEmbedCodeQuery,
    { fetchPolicy: "cache-first" }
  );
  const baseEmbedCode = embedData?.content?.embed;

  const shareUrl =
    startSeconds !== null
      ? urlWithStart(baseShareUrl, startSeconds)
      : baseShareUrl;
  const embedCode =
    startSeconds !== null
      ? embedWithStart(baseEmbedCode, startSeconds)
      : baseEmbedCode;

  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  // Открытие окна: сбрасываем состояние и подставляем текущую секунду
  // плеера, как раньше делал обработчик клика по кнопке-триггеру.
  useEffect(() => {
    if (!open) return;

    setCopied(null);

    if (getCurrentTime) {
      setStartEnabled(false);
      setStartText(durationFormat(Math.floor(getCurrentTime() || 0)));
    }

    if (contentId) {
      loadEmbedCode({ variables: { id: contentId } });
    }
    // Хотим перезапускать только по факту открытия, а не на каждое
    // изменение getCurrentTime/contentId/loadEmbedCode.
    // eslint-disable-next-line
  }, [open]);

  const markCopied = (what) => {
    setCopied(what);
    clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopied(null), COPIED_TIMEOUT);
  };

  const onCopyUrl = async () => {
    if (await copyText(shareUrl)) {
      markCopied("url");
    }
  };

  const onCopyEmbed = async () => {
    if (await copyText(embedCode)) {
      markCopied("embed");
    }
  };

  const onNativeShare = async () => {
    try {
      // text не передаём: на iOS он ломает ссылку
      // https://adactio.com/journal/15972
      await navigator.share({ title: shareText, url: shareUrl });
    } catch (err) {
      console.log(err);
    }
  };

  const onBlurStartText = () => {
    const seconds = parseStartTime(startText);
    setStartText(durationFormat(seconds === null ? 0 : seconds));
  };

  const selectAll = (event) => event.target.select();

  const gap = compact ? 1 : 2;
  const startInvalid = startEnabled && startSeconds === null;

  const copyUrlButton = (
    <CopyButton
      done={copied === "url"}
      onClick={onCopyUrl}
      doneText={t("shareButton.urlCopied")}
    >
      {t("shareButton.copyLink")}
    </CopyButton>
  );

  const copyEmbedButton = embedCode && (
    <CopyButton
      done={copied === "embed"}
      onClick={onCopyEmbed}
      doneText={t("shareButton.codeCopied")}
    >
      {t("shareButton.copyEmbedCode")}
    </CopyButton>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      // Клик внутри окна не должен всплыть до плеера и запустить видео
      onClick={(event) => event.stopPropagation()}
      fullScreen={fullScreen}
      maxWidth={false}
      disableScrollLock={disableScrollLock}
      scroll="paper"
      BackdropProps={{ sx: { backgroundColor: UI.overlay } }}
      PaperProps={{
        sx: {
          width: fullScreen ? "100%" : 469,
          maxWidth: "100%",
          m: fullScreen ? 0 : 1,
          maxHeight: fullScreen ? "100%" : "calc(100% - 16px)",
          backgroundColor: UI.bg,
          backgroundImage: "none",
          borderRadius: fullScreen ? 0 : "16px",
          color: "#fff",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          overflowY: "auto",
          p: compact
            ? "10px 12px"
            : fullScreen
            ? "14px 16px 20px"
            : "16px 20px 24px",
        }}
      >
        {/* В карточке на десктопе системное меню иконкой в шапке, чтобы
            окно влезало в эмбед 744×419 без прокрутки */}
        {canNativeShare && !fullScreen && (
          <Tooltip title={t("shareButton.moreApps")} arrow>
            <IconButton
              aria-label={t("shareButton.moreApps")}
              onClick={onNativeShare}
              size="small"
              sx={{ position: "absolute", top: 10, right: 44, color: "#fff" }}
            >
              <IosShareIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}

        <IconButton
          aria-label={t("close")}
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: compact ? 4 : 10,
            right: compact ? 4 : 10,
            color: "#fff",
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>

        <Typography
          sx={{
            pr: canNativeShare && !fullScreen ? 9 : 4,
            mb: compact ? 1 : 3,
            fontSize: compact ? 16 : 18,
            lineHeight: "24px",
            fontWeight: 500,
          }}
        >
          {t("shareButton.title")}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap,
            ...(compact && {
              "& .MuiButtonBase-root:not(.MuiCheckbox-root)": {
                minHeight: 36,
                py: 0.5,
              },
            }),
          }}
        >
          {getCurrentTime && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Checkbox
                size="small"
                checked={startEnabled}
                onChange={(event) => setStartEnabled(event.target.checked)}
                sx={{
                  ml: -1,
                  p: 0.5,
                  color: UI.border,
                  "&.Mui-checked": { color: UI.primary },
                }}
                inputProps={{ "aria-label": t("shareButton.startAt") }}
              />
              <Label component="span">{t("shareButton.startAt")}</Label>
              <Field
                value={startText}
                onChange={(event) => setStartText(event.target.value)}
                onBlur={onBlurStartText}
                onFocus={() => setStartEnabled(true)}
                inputProps={{
                  inputMode: "numeric",
                  "aria-label": t("shareButton.startAt"),
                  style: { textAlign: "center" },
                }}
                sx={{
                  width: 84,
                  height: 32,
                  fontSize: 14,
                  ...(startInvalid && { borderColor: UI.error }),
                }}
              />
            </Box>
          )}

          {compact ? (
            // Совсем низкий эмбед: только кнопки, без полей с текстом
            <>
              {copyUrlButton}
              {copyEmbedButton}
            </>
          ) : (
            <>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Label>{t("shareButton.videoLink")}</Label>
                <Field
                  value={shareUrl}
                  readOnly
                  onFocus={selectAll}
                  inputProps={{ "aria-label": t("shareButton.videoLink") }}
                />
              </Box>
              {copyUrlButton}

              {embedCode && (
                <>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Label>{t("shareButton.embedLabel")}</Label>
                    <Field
                      value={embedCode}
                      readOnly
                      onFocus={selectAll}
                      inputProps={{
                        "aria-label": t("shareButton.embedLabel"),
                      }}
                    />
                  </Box>
                  {copyEmbedButton}
                  <Label>{t("shareButton.embedHint")}</Label>
                </>
              )}
            </>
          )}

          {canNativeShare && fullScreen && (
            <ActionButton outlined onClick={onNativeShare}>
              <IosShareIcon />
              {t("shareButton.moreApps")}
            </ActionButton>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}

ShareDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  contentId: PropTypes.string,
  shareText: PropTypes.string.isRequired,
  shareUrl: PropTypes.string.isRequired,
  getCurrentTime: PropTypes.func,
  disableScrollLock: PropTypes.bool,
};

export default ShareDialog;
